import type { BookingParts, LabelledText } from './read'
import { MONTH_PATTERN, monthOf, plainWord, relativeDay, ROUTE_FROM, ROUTE_TO, START_TERMS } from './ticketWords'

/**
 * A booking's times worked out by code (docs/screenshot-cleanup/PLAN.md, phase 2): the model copies "Sat 24 Oct 2026 · Doors 19:00 ·
 * Show 20:00" as shown, and this reads it. A missing year is the first one that puts the date no more than two months before the
 * screenshot was taken; a numeric date that could be either day-first or month-first ("03/11") is not guessed. The start time is the
 * one labelled show, start or departs when there are several; otherwise the only or first one.
 */
export interface BookingTime {
  /** Local time, as a timestamp. With no time shown, midday, so a day before or after never crosses midnight. */
  at: number
  date: string
  time: string | null
}

const LOOKBACK_DAYS = 60
// Dates, times and start labels in every language ticketWords.ts supports ("25. Okt. 2026", "25 de octubre", "20h30", "20 Uhr", "Beginn").
const START_LABEL = new RegExp(String.raw`(?<![\p{L}])(?:${[...START_TERMS, 'appointment', 'reservation'].map(word => plainWord(word).replace(/[-\s]/g, '[-\\s]?')).join('|')})(?![\p{L}])`, 'iu')
const DAY_FIRST = new RegExp(String.raw`(?<!\d)(\d{1,2})(?:st|nd|rd|th|\.)?\s+(?:de\s+)?${MONTH_PATTERN},?(?:\s+(?:de\s+)?(\d{4}))?`, 'giu')
const MONTH_FIRST = new RegExp(String.raw`${MONTH_PATTERN}\s+(\d{1,2})(?:st|nd|rd|th)?(?!\d),?(?:\s+(\d{4}))?`, 'giu')
// An airline's e-ticket writes the day, month and year together: "25Oct2026".
const COMPACT = /(?<!\d)(\d{1,2})(\p{L}{3})(\d{4})(?!\d)/gu
/** A clock time as written: "19:00", "8.30pm", "8 PM", "20h30", "20h", "20 Uhr" (not "12.10" inside the date "12.10.2026"). */
export const CLOCK = /(?<![\d.:])(\d{1,2})(?:[:.](\d{2})|(h)(\d{2})?)?\s*(am|pm|a\.m\.|p\.m\.|uhr)?(?![\d/\p{L}]|\.\d)/giu

const pad = (n: number) => String(n).padStart(2, '0')

function withYear(month: number, day: number, year: number | null, capturedAt: number): { y: number; m: number; d: number } | null {
  if (month < 0 || month > 11 || day < 1 || day > 31) return null
  if (year !== null) return { y: year < 100 ? 2000 + year : year, m: month, d: day }
  const captured = new Date(capturedAt)
  const earliest = capturedAt - LOOKBACK_DAYS * 86_400_000
  for (const y of [captured.getFullYear() - 1, captured.getFullYear(), captured.getFullYear() + 1]) {
    if (new Date(y, month, day, 12).getTime() >= earliest) return { y, m: month, d: day }
  }
  return null
}

/** The calendar date in a piece of text, or null when there is none or it cannot be told apart. */
export function readDate(text: string, capturedAt: number): { y: number; m: number; d: number } | null {
  const iso = /\b(\d{4})-(\d{2})-(\d{2})\b/.exec(text)
  if (iso) return withYear(Number(iso[2]) - 1, Number(iso[3]), Number(iso[1]), capturedAt)
  for (const match of [...text.matchAll(COMPACT), ...text.matchAll(DAY_FIRST)]) {
    const month = monthOf(match[2]!)
    if (month !== null) return withYear(month, Number(match[1]), match[3] ? Number(match[3]) : null, capturedAt)
  }
  for (const match of text.matchAll(MONTH_FIRST)) {
    const month = monthOf(match[1]!)
    if (month !== null) return withYear(month, Number(match[2]), match[3] ? Number(match[3]) : null, capturedAt)
  }
  const numeric = /\b(\d{1,2})[./](\d{1,2})[./](\d{2,4})\b/.exec(text)
  if (numeric) {
    const a = Number(numeric[1]), b = Number(numeric[2]), year = Number(numeric[3])
    if (a > 12 && b <= 12) return withYear(b - 1, a, year, capturedAt)
    if (b > 12 && a <= 12) return withYear(a - 1, b, year, capturedAt)
    if (a === b) return withYear(a - 1, b, year, capturedAt)
  }
  // A confirmation that names only the day ("Wednesday at 2:00 pm", "Tomorrow", "Morgen") means the coming one.
  const captured = new Date(capturedAt)
  const ahead = relativeDay(text, captured.getDay())
  if (ahead !== null) {
    const day = new Date(captured.getFullYear(), captured.getMonth(), captured.getDate() + ahead, 12)
    return { y: day.getFullYear(), m: day.getMonth(), d: day.getDate() }
  }
  return null
}

interface FoundTime { minutes: number; index: number }

/** Every clock time in a piece of text, in order ("19:00", "8.30pm", "8 PM", "20h30", "20 Uhr"). */
export function readTimes(text: string): FoundTime[] {
  const found: FoundTime[] = []
  for (const match of text.matchAll(CLOCK)) {
    const written = match[2] ?? match[4]
    const hours = Number(match[1]), minutes = written ? Number(written) : 0, mark = match[5]?.toLowerCase().replace(/\./g, '')
    const meridiem = mark === 'am' || mark === 'pm' ? mark : undefined
    if (!written && !match[3] && !mark) continue
    if (minutes > 59 || hours > 23 || (meridiem && (hours < 1 || hours > 12))) continue
    const h = meridiem === 'pm' ? (hours % 12) + 12 : meridiem === 'am' ? hours % 12 : hours
    found.push({ minutes: h * 60 + minutes, index: match.index! })
  }
  return found
}

/** The start time in a text: the one right after a start label when there are several, else the only or first one. */
function startTime(text: string): number | null {
  const times = readTimes(text)
  if (!times.length) return null
  if (times.length === 1) return times[0]!.minutes
  const labelled = times.find(time => START_LABEL.test(plainWord(text.slice(Math.max(0, time.index - 18), time.index))))
  return (labelled ?? times[0]!).minutes
}

const asTime = (date: { y: number; m: number; d: number }, minutes: number | null): BookingTime => ({
  at: new Date(date.y, date.m, date.d, minutes === null ? 12 : Math.floor(minutes / 60), minutes === null ? 0 : minutes % 60).getTime(),
  date: `${date.y}-${pad(date.m + 1)}-${pad(date.d)}`,
  time: minutes === null ? null : `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`,
})

/**
 * When the booking starts: the date from "when", the time from "when" or, failing that, from a labelled start time (departs, show).
 * A boarding or doors time alone is not the start.
 */
export function bookingStart(booking: Pick<BookingParts, 'when' | 'times'>, capturedAt: number): BookingTime | null {
  if (!booking.when) return null
  const date = readDate(booking.when, capturedAt)
  if (!date) return null
  const fromWhen = startTime(booking.when)
  const labelled = booking.times.find(entry => entry.label && START_LABEL.test(plainWord(entry.label)))
  const fromTimes = labelled ? startTime(labelled.text) : null
  return asTime(date, fromWhen ?? fromTimes)
}

/** A deadline's moment ("Free cancellation until Thu 22 Oct, 19:30"); its year follows the booking's when the text has none. */
export function deadlineAt(deadline: LabelledText, capturedAt: number): BookingTime | null {
  const date = readDate(deadline.text, capturedAt)
  if (!date) return null
  return asTime(date, startTime(deadline.text))
}

export interface BookingSuggestions {
  /** Quick Access from the start of the day before to the end of the day after (decision 8). */
  quickAccess: { from: number; until: number } | null
  /** A reminder the day before at 09:00, when that is still ahead. */
  reminder: { at: number } | null
}

export function suggestionsFor(start: BookingTime | null, now = Date.now()): BookingSuggestions {
  if (!start) return { quickAccess: null, reminder: null }
  const day = new Date(start.at)
  const from = new Date(day.getFullYear(), day.getMonth(), day.getDate() - 1).getTime()
  const until = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 2).getTime()
  const remind = new Date(day.getFullYear(), day.getMonth(), day.getDate() - 1, 9, 0).getTime()
  return { quickAccess: until > now ? { from, until } : null, reminder: remind > now ? { at: remind } : null }
}

export interface WhenTime { label: string | null; minutes: number; /** The check's field name, to mark it when it was not confirmed. */ field: string }
export interface WhenBlock { date: { y: number; m: number; d: number }; times: WhenTime[] }

/**
 * A booking's date and every labelled time in one place, earliest first ("Sat 24 Oct" over "Doors 19:00 · Show 20:00"): the date and
 * times as code read them from "when" and "times". Null when the date cannot be read for certain, so the card shows the text as written.
 * A time the code cannot read keeps its own row as written.
 */
export function whenBlock(booking: Pick<BookingParts, 'when' | 'times'>, capturedAt: number): WhenBlock | null {
  if (!booking.when) return null
  const date = readDate(booking.when, capturedAt)
  if (!date) return null
  const times: WhenTime[] = []
  // "Sat 24 Oct 2026 · Doors 19:00 · Show 20:00": each part with a time gives its own label, the words left once the date and time are gone.
  for (const part of booking.when.split(/\s*[·|•,–—]\s*|\s+-\s+/)) {
    const found = readTimes(part)
    if (found.length !== 1) continue
    const label = part.replace(new RegExp(CLOCK.source, 'iu'), '').replace(/(?<!\p{L})(?:at|from|um|ab|à|a las|desde|alle|dalle|ore)(?!\p{L})/giu, '').replace(/\s+/g, ' ').trim()
    times.push({ label: label && !readDate(label, capturedAt) && !/\d/.test(label) && label.length <= 30 ? label : null, minutes: found[0]!.minutes, field: 'when' })
  }
  booking.times.forEach((entry, i) => {
    const found = readTimes(entry.text)
    if (found.length === 1) times.push({ label: entry.label, minutes: found[0]!.minutes, field: `times.${i}` })
  })
  const seen = new Set<number>()
  return { date, times: times.sort((a, b) => a.minutes - b.minutes).filter(time => (seen.has(time.minutes) ? false : (seen.add(time.minutes), true))) }
}

const flatText = (text: string) => text.normalize('NFKD').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '')
const isHeading = (lines: readonly string[]) => lines.length === 1 && lines[0]!.split(/\s+/).length <= 5 && !/[.!?:,;]$/.test(lines[0]!)

/**
 * The ticket's own notices, the words the fields do not hold ("Important note: passengers are required…", "Carry-on", "Baggage"), as
 * Markdown under the fields: each paragraph with a sentence in it, under the short heading above it. The ticket's face (names, route,
 * seat, times) is the fields' job and is left out, and so is any line a field already shows (a "before you go" step, a gate time).
 * Read only from the item's text, so a booking unpacked before this shows its notices too.
 */
export function ticketNotes(booking: BookingParts, text: string, capturedAt: number): string {
  const held = [booking.what, booking.with, booking.where, booking.reference, booking.people, ...booking.bring, ...booking.seats,
    ...booking.times.map(entry => entry.text), ...booking.deadlines.map(entry => `${entry.label ?? ''} ${entry.text}`)].filter((part): part is string => Boolean(part)).map(flatText).filter(Boolean)
  const shownTimes = new Set(whenBlock(booking, capturedAt)?.times.map(time => time.minutes) ?? [])
  const what = booking.what ? flatText(booking.what) : ''
  const shown = (line: string) => {
    // A line that is only a link (a shared message's "[Restaurant](…)"): the pass has the place's own links.
    if (/^\[[^\]]*\]\([^)]*\)$/.test(line.trim())) return true
    // A short line restating what the booking is ("I booked a table for 2 at Sticks'n'Sushi"): the pass says it.
    if (what.length >= 4 && flatText(line).includes(what) && line.split(/\s+/).length <= 20) return true
    const bare = flatText(line.replace(/^[-*•]\s*/, ''))
    if (!bare || held.some(part => part === bare || (part.length >= 6 && bare.includes(part) && part.length >= bare.length * 0.6))) return true
    // A short line about a time the card already shows ("Gate opens at 19:55").
    const times = readTimes(line).map(time => time.minutes)
    return times.length > 0 && times.every(time => shownTimes.has(time)) && line.split(/\s+/).length <= 8
  }
  // The booking app's own features are not the ticket's notices (OpenTable: "view your bill", points to earn, AI questions).
  const appFeature = /\b(?:view (?:your|the) bill|points? (?:to be )?earned|earn \d+ points|powered by ai|ask anything|manage (?:your )?booking|browse (?:the )?menu|must-order dishes|best-kept secrets|download (?:the|our) app)\b/i
  const blocks = text.split(/\n\s*\n/).map(block => block.split('\n').map(line => line.trim()).filter(Boolean)).filter(block => block.length && !block.some(line => appFeature.test(line)))
  const out: string[] = []
  blocks.forEach((block, i) => {
    const kept = block.filter(line => !shown(line))
    // A notice holds a sentence; a block of short label-and-value lines is the ticket's face.
    if (!kept.some(line => line.replace(/^[-*•]\s*/, '').split(/\s+/).length >= 8)) return
    const before = blocks[i - 1]
    if (before && isHeading(before)) out.push(`## ${before[0]}`)
    out.push(kept.join('\n'))
  })
  return out.join('\n\n')
}

/** A short labelled line ("Departure 21:25 25 Oct 26", "Von: Berlin"): the label as written, with what follows it, when the label is one of these. */
function labelledLine(line: string, labels: readonly string[]): { label: string; rest: string } | null {
  const words = line.trim().split(/\s+/)
  if (words.length > 8) return null
  for (const count of [3, 2, 1]) {
    if (words.length <= count) continue
    const label = words.slice(0, count).join(' ').replace(/:$/, '')
    if (labels.includes(plainWord(label))) return { label, rest: words.slice(count).join(' ').replace(/^:\s*/, '').trim() }
  }
  return null
}

/**
 * A booking with what the reading left out but the ticket's own text holds, in the languages ticketWords.ts supports: the start time
 * from a labelled line ("Departure 21:25", "Abflug 21:25", "Départ 21h25") when "when" has a date and no time and no start time is
 * listed, and the route from the "From" and "To" lines when there is no place ("From BER Terminal 1" / "To TLV" → "BER Terminal 1 to
 * TLV", taking the route off the end of a title it was put in). Only lines of the item's own text are used; nothing is worked out,
 * with one narrow exception: when the text has the start label and the date but no time ("Departure 25 Oct 26"), and the check copied a
 * bare time right beside that same date ("21:25" next to "25 Oct 26"), that time is the start.
 */
export function completeBooking(booking: BookingParts, text: string, capturedAt: number, checkLines: readonly string[] = []): BookingParts {
  const lines = text.split('\n').map(line => line.replace(/^[-*•]\s*/, '').trim()).filter(Boolean)
  let next = booking
  const date = booking.when ? readDate(booking.when, capturedAt) : null
  const startListed = booking.times.some(entry => entry.label && START_LABEL.test(plainWord(entry.label)))
  if (date && !readTimes(booking.when!).length && !startListed) {
    for (const line of lines) {
      const found = labelledLine(line, START_TERMS.map(plainWord))
      if (!found) continue
      const times = readTimes(found.rest)
      const day = readDate(found.rest, capturedAt)
      if (times.length !== 1 || (day && (day.y !== date.y || day.m !== date.m || day.d !== date.d))) continue
      const written = new RegExp(CLOCK.source, 'iu').exec(found.rest)?.[0].trim() ?? ''
      next = { ...next, times: [{ label: found.label, text: written }, ...next.times] }
      break
    }
    const sameDay = (line: string) => { const day = readDate(line, capturedAt); return day !== null && day.y === date.y && day.m === date.m && day.d === date.d }
    const dated = next === booking ? lines.map(line => labelledLine(line, START_TERMS.map(plainWord))).find(found => found && sameDay(found.rest) && !readTimes(found.rest).length) : null
    if (dated) {
      const bare = (line: string | undefined) => line !== undefined && /^\s*\d{1,2}(?:[:.h]\d{2})\s*(?:uhr)?\s*$/i.test(line)
      const at = checkLines.findIndex(line => sameDay(line) && !readTimes(line).length)
      const time = at < 0 ? undefined : [checkLines[at - 1], checkLines[at + 1]].find(bare)
      if (time) next = { ...next, times: [{ label: dated.label, text: time.trim() }, ...next.times] }
    }
  }
  if (!booking.where) {
    const end = (labels: readonly string[]) => lines.map(line => labelledLine(line, labels)).find(found => found && found.rest && found.rest.length <= 40 && !readTimes(found.rest).length && !readDate(found.rest, capturedAt))?.rest
    const from = end(ROUTE_FROM), to = end(ROUTE_TO)
    if (from && to) {
      // "LY2374 BER to TLV": the route the title holds goes to Where; the title keeps what comes before it.
      const what = booking.what
      const cut = what ? what.indexOf(from.split(/\s+/)[0]!) : -1
      const tail = what && cut > 0 ? what.slice(cut) : ''
      next = { ...next, where: `${from} to ${to}`, ...(tail && plainWord(tail).includes(plainWord(to)) ? { what: what!.slice(0, cut).trim() } : {}) }
    }
  }
  return next
}
